import React, { useEffect, useMemo } from 'react';
import { debounce, isFinite } from 'lodash';
import { useResizeObserver } from 'usehooks-ts';

import WindForecastData from 'components/WindForecastData';
import type { ConditionRating } from 'types/conditionRating';
import type { LiveWindPoint } from 'types/liveWind';
import type { WindData } from 'types/wind';

import useSetupWindBarGraphCanvas from './useSetupWindBarGraphCanvas';

import styles from './WindForecastGraphContainer.module.scss';

interface Props {
  activeItem: {
    day: number;
    hour: number;
    condition?: ConditionRating;
  };
  day: number;
  displayOrderIndex: number;
  isLive: boolean;
  wind: WindData;
  liveWind: Array<LiveWindPoint> | null;
  isMobile: boolean;
}
const WindForecastGraphContainer: React.FC<Props> = ({
  activeItem,
  day,
  displayOrderIndex,
  isLive,
  wind,
  liveWind,
  isMobile,
}) => {
  const windData = isLive && liveWind?.length ? liveWind : wind?.days?.[day];

  const canvasRef = React.useRef<HTMLDivElement | null>(null);

  const { width: graphWidth = 320 } = useResizeObserver({
    ref: canvasRef,
  });

  const maxWindSpeed = useMemo(() => {
    if (liveWind?.length) {
      return Math.max(
        ...(wind?.days?.flat(1).map((reading) => reading?.speed || 0) || []),
        ...liveWind.map((reading) => reading?.wind?.speed || 0),
      );
    }
    return Math.max(...wind.days.flat(1).map((reading) => reading?.speed || 0));
  }, [liveWind, wind.days]);

  const { setUpBarGraph, barWidth, barSpacing } = useSetupWindBarGraphCanvas({
    windData,
    maxWindSpeed,
    units: wind.units,
    activeItem,
    graphWidth,
    day,
    displayOrderIndex,
    isMobile,
  });

  const debouncedSetUpBarGraph = useMemo(
    () => debounce(setUpBarGraph, 100, { leading: true, trailing: true }),
    [setUpBarGraph],
  );

  useEffect(() => {
    if (graphWidth) debouncedSetUpBarGraph();

    return () => {
      debouncedSetUpBarGraph.cancel();
    };
  }, [graphWidth, debouncedSetUpBarGraph]);

  return (
    <>
      <div
        className={styles.windBarGraphContainer}
        data-testid={`${isLive ? 'live-' : ''}wind-bar-graph`}
      >
        <div
          className={styles.windBarGraphCanvas}
          id={`wind-bar-graph-canvas-day-${day}`}
          ref={canvasRef}
        />
      </div>
      {isFinite(graphWidth) && (
        <WindForecastData
          barSpacing={barSpacing}
          barWidth={barWidth}
          graphWidth={graphWidth}
          isLive={isLive}
          isOnshoreOffshoreGraph={false}
          wind={windData}
        />
      )}
    </>
  );
};

export default WindForecastGraphContainer;
